import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useDispatch } from 'react-redux';

// Constants
import ExternalLinksConstants from 'Shared/constants/ExternalLinks.constants';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Actions
import * as SubscriptionsActions from 'Shared/actions/Subscriptions.actions';

import { WpContainer, WpContainerBody, WpContainerHeader, WpContainerFooter } from 'Wpds/components/container';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import CreditCardDisplay from 'Dashboard/components/CreditCardDisplay/CreditCardDisplay';
import CreditCardInput from 'Dashboard/components/CreditCardInput/CreditCardInput';

// Styles
import './CreditCardInfo.scss';

type Props = {
  setActiveSubscription: Function;
  subscription?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
    next_billing_date: number;
    upcoming_invoice: {
      amount: number;
    };
  };
};

function CreditCardInfo({ subscription, setActiveSubscription }: Props) {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();

  const [showUpdateCardInfo, toggleShowUpdateCardInfo] = useToggle(false);
  const [isSaving, toggleIsSaving] = useToggle(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const hasCard = subscription?.brand && subscription?.last4;

  const handleUpdateCreditCard = async () => {
    if (!stripe || !elements) {
      return;
    }

    toggleIsSaving();
    setErrorMessage(null);
    const { error, token } = await stripe.createToken(elements.getElement(CardElement));

    if (error) {
      setErrorMessage(error.message);
      toggleIsSaving();
      return;
    }

    dispatch(SubscriptionsActions.updateCreditCard({ stripeToken: token.id }))
      .then(data => {
        setActiveSubscription(data);
        toggleShowUpdateCardInfo();
      })
      .catch(err => {
        setErrorMessage(err.response.data.error);
      })
      .finally(() => {
        toggleIsSaving();
      });
  };

  const baseClassName = 'CreditCardInfo';

  return (
    <>
      {subscription && hasCard && (
        <WpContainer>
          <WpContainerHeader>
            <h5>Credit Card</h5>
          </WpContainerHeader>
          <WpContainerBody>
            <div className={baseClassName}>
              {showUpdateCardInfo ? (
                <div className={`${baseClassName}__update-card`}>
                  <CreditCardInput setErrorMessage={setErrorMessage} errorMessage={errorMessage} />
                </div>
              ) : (
                <CreditCardDisplay
                  brand={subscription?.brand}
                  last4={subscription?.last4}
                  expireMonth={subscription?.exp_month}
                  expireYear={subscription?.exp_year}
                />
              )}
            </div>
          </WpContainerBody>
          <WpContainerFooter>
            {showUpdateCardInfo ? (
              <WpButtonGroup>
                <WpButton onClick={handleUpdateCreditCard} loading={isSaving}>
                  Update
                </WpButton>
                <WpButton variant="tertiary" onClick={toggleShowUpdateCardInfo} disabled={isSaving}>
                  Cancel
                </WpButton>
              </WpButtonGroup>
            ) : (
              <WpButton variant="tertiary" onClick={toggleShowUpdateCardInfo}>
                Update Credit Card
              </WpButton>
            )}
          </WpContainerFooter>
        </WpContainer>
      )}
      {subscription && !hasCard && (
        <WpContainer>
          <WpContainerHeader>
            <h5>Payment Method</h5>
          </WpContainerHeader>
          <WpContainerBody>
            This account is set up to pay by invoice. If you have any questions{' '}
            <a href={ExternalLinksConstants.contact_us} target="_blank" rel="noopener noreferrer">
              contact us.
            </a>
          </WpContainerBody>
        </WpContainer>
      )}
    </>
  );
}

export default CreditCardInfo;
